import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { ExternalLink } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { Typography } from '@/components/ui/typography';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';
import type { PMSVendorData } from '@/types/pms.types';
import {
  saveConnectionAPI,
  testVendorConnectionSyncAPI,
} from '../../pms-configuration/service/configurationApiService';
import { getSyncLogsAPI } from '../../service/PMSVendorsApiService';
import { connectedConnection, failedConnection } from '../../pms-configuration/types/connection-state';
import { CredentialSummary, credentialRows } from '../../components/CredentialSummary';
import { credentialKindFor } from '../credentialKind';
import type { ConnectionPanelProps } from '../types';

export const BUILDIUM_INSTALL_URL =
  'https://signin.managebuilding.com/Manager/app/marketplace/apps/fb447fb7-a2a1-4a75-9f64-18f423a4ba74/install';
const BUILDIUM_MARKETPLACE_URL = 'https://marketplace.buildium.com/';
const DATE_PLACEHOLDER = '-----';

const removeTokenFromUrl = () => {
  const cleanHash = window.location.hash.split('?')[0];
  window.history.replaceState(null, '', `${window.location.pathname}${cleanHash}`);
};

/** Read mode's rows before the stored record arrives. */
const NO_CREDENTIALS = { url: '', key: '', secret: '' };

/**
 * Buildium's credentials panel. Both vendor-owned async spans raise
 * `ctx.isPanelBusy` (the page-level spinner `VendorSettingsLayout` shows in
 * place of its normal chrome, reproducing the old page's single unified
 * `loading`):
 *
 * 1. The `?token=` OAuth-return save (`shouldBootstrap` is false on this
 *    route, so `ctx.isBootstrapping` never covers it). A non-200 response
 *    leaves the spinner up forever — today's behavior, pinned by the
 *    characterization suite. The URL's `?token=` is stripped unconditionally
 *    (success, failure, or non-200) via `finally`, matching the old page —
 *    it's the user's only way back to a normal page load on a reload.
 * 2. Once the shared bootstrap GET seeds `ctx.vendorData`, the sync-log +
 *    connection-test follow-on the old page made in the same unified
 *    `loading` span.
 *
 * Both are safe to route through `ctx.isPanelBusy` specifically because
 * `VendorSettingsLayout` keeps `<ConnectionPanel>` mounted while
 * `isPanelBusy` is true (it hides the chrome via CSS instead of discarding
 * the tree) — this panel is never unmounted by its own busy flag, so neither
 * effect's `cancelled`/no-op-on-unmount guard can strand the flag raised.
 */
const BuildiumConnectionPanel = ({ ctx }: ConnectionPanelProps) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const {
    account,
    vendorData,
    connectionState,
    setConnectionState,
    setToggleLoader,
    setPanelBusy,
    redirectToHowToConfigure,
    submitRef,
    saveAndSync,
    saveWithoutSync,
    integrationStatus,
    editedSettings,
    mode,
  } = ctx;
  const [lastSyncDate, setLastSyncDate] = useState(DATE_PLACEHOLDER);
  const [intEnableDate, setIntEnableDate] = useState(DATE_PLACEHOLDER);

  // OAuth return: persist the marketplace token. Today's page does NOT refetch
  // the vendor record afterwards (it renders the idle layout) — preserved.
  useEffect(() => {
    if (!token) return;
    setPanelBusy(true);
    (async () => {
      try {
        const resp = await saveConnectionAPI(token);
        if (resp?.status?.toString() === '200') {
          dispatch(
            showAlert({
              type: Variants.SUCCESS,
              message: 'Integration successful. Property sync in progress...',
            }),
          );
          setPanelBusy(false);
        }
        // non-200: spinner stays up forever (today's behavior, asserted by the
        // characterization suite).
      } catch {
        dispatch(showAlert({ type: Variants.ERROR, message: 'Integration failed. Please try again.' }));
        setPanelBusy(false);
      } finally {
        // Unconditional, matching the old page's `finally` — the URL is
        // cleaned up even when the save resolves non-200 and the spinner is
        // left stuck, since a reload is the user's only way out of that state.
        removeTokenFromUrl();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Connected record: dates are best-effort, then the connection test decides
  // the header state.
  useEffect(() => {
    if (!vendorData) return;
    let cancelled = false;
    (async () => {
      setPanelBusy(true);
      setToggleLoader(true);
      try {
        const syncData = await getSyncLogsAPI(account);
        if (!cancelled && syncData.data && syncData.data.endDate) {
          setLastSyncDate(dayjs(new Date(syncData.data.endDate * 1000)).format('MMM DD, YYYY'));
        }
        if (!cancelled) setIntEnableDate(dayjs(vendorData.createdDate).format('MMM DD, YYYY'));
      } catch {
        // sync-log fetch is best-effort; the page still renders without it
      }
      try {
        // masked means keep, server-side: a masked or blank key/secret is replaced by the stored credential in property-service, matched on entity and vendor (DV-3414)
        const resp = await testVendorConnectionSyncAPI(
          account,
          vendorData,
          vendorData.autoSyncAssets,
          vendorData.watermarkedPhotos,
          vendorData.pmsSource,
        );
        if (!cancelled && resp?.data?.status === 'Success') setConnectionState(connectedConnection());
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } }).response?.status;
        if (!cancelled && status) setConnectionState(failedConnection());
      } finally {
        // setPanelBusy is ctx (parent-hook) state, so setting it after
        // unmount is safe — and unconditional is what removes the last
        // permanent-spinner path by construction, rather than by argument
        // about which unmount windows are reachable today.
        setPanelBusy(false);
        if (!cancelled) {
          setToggleLoader(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorData]);

  // Connected Buildium has no credentials form: the bar's requestSubmit
  // reaches the same direct save the old footer button ran (no credential
  // test — the marketplace token isn't something the user can paste).
  const saveStored = useCallback(() => {
    // masked means keep, server-side: a masked or blank key/secret is replaced by the stored credential in property-service, matched on entity and vendor (DV-3414)
    const configData: PMSVendorData = {
      url: vendorData?.url ?? '', key: vendorData?.key ?? '', secret: vendorData?.secret ?? '',
      pmsSource: vendorData?.pmsSource, accountId: vendorData?.accountId, ...editedSettings,
    };
    if (integrationStatus.kind === 'disabled') void saveWithoutSync(configData);
    else saveAndSync(configData);
  }, [editedSettings, integrationStatus.kind, saveAndSync, saveWithoutSync, vendorData]);

  // Registered unconditionally, with `idle` writing null rather than returning
  // early: an early return also skips the cleanup, so a connected/failed → idle
  // transition (a re-run connection test resetting the state) would leave the
  // previous `saveStored` armed in `submitRef` while the panel shows the
  // "Connect with Buildium" idle body (review finding 8).
  useEffect(() => {
    submitRef.current = connectionState.kind === 'idle' ? null : saveStored;
    return () => {
      submitRef.current = null;
    };
  }, [connectionState.kind, saveStored, submitRef]);

  const isConnected = connectionState.kind === 'connected';
  const isFailed = connectionState.kind === 'failed';
  const isNotConnected = connectionState.kind === 'idle';
  const storedCredentials = vendorData ?? NO_CREDENTIALS;

  return (
    <>
      {/* The vendor-logo / connector / PlanOmatic block is gone (prototype); the
          header's status label carries the connection state. Buildium's failed
          state had its "check the integration" link inside that block, so it is
          rehomed here as the panel's own line — the only place it appeared. */}
      {isFailed && (
        <div className="px-4 pt-4">
          <Typography variant="body">
            <span className="font-semibold text-destructive">Connection Inactive.</span> Go to{' '}
            <a
              href={BUILDIUM_MARKETPLACE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-destructive"
            >
              Buildium marketplace
            </a>{' '}
            and check the integration
          </Typography>
        </div>
      )}
      {isNotConnected && (
        <div className="flex flex-col gap-4 p-4">
          <div>
            <Typography variant="body">Refer to the </Typography>
            <Button type="button" variant="link" onClick={redirectToHowToConfigure}>
              {' guide '}
            </Button>
            <Typography variant="body"> in the Integration Overview tab for step-by-step instructions.</Typography>
          </div>
          <ul className="list-disc pl-5 flex flex-col gap-2">
            <li>
              <Typography variant="body">Click </Typography>
              <Typography variant="body-semibold">Connect with Buildium</Typography>
              <Typography variant="body"> below to get started</Typography>
            </li>
            <li>
              <Typography variant="body">
                You will be asked for your Buildium login to confirm that your user role is permitted to install
                Marketplace integrations to your Buildium account
              </Typography>
            </li>
          </ul>
          <div>
            <Button type="button" onClick={() => window.open(BUILDIUM_INSTALL_URL, '_blank', 'noopener')}>
              Connect with Buildium
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
          <div className="flex flex-col gap-1">
            <Typography variant="body" className="text-muted-foreground">
              Integration Status
            </Typography>
            <StatusPill variant="muted">Not Enabled</StatusPill>
          </div>
        </div>
      )}
      {(isConnected || isFailed) && (
        <div className="flex flex-col gap-4 p-4">
          {mode === 'read' && (
            <CredentialSummary
              vendorName="Buildium"
              rows={credentialRows(credentialKindFor('buildium', storedCredentials), 'Buildium', storedCredentials)}
            />
          )}
          <div className="flex flex-col gap-1">
            <Typography variant="body" className="text-muted-foreground">
              Integration Status
            </Typography>
            {isFailed && <StatusPill variant="destructive">Inactive</StatusPill>}
            {isConnected && <StatusPill variant="success">Active</StatusPill>}
          </div>
          <div className="flex flex-col gap-1">
            <Typography variant="body" className="text-muted-foreground">
              Integration Enabled
            </Typography>
            <Typography variant="body-semibold">{intEnableDate}</Typography>
          </div>
          <div className="flex flex-col gap-1">
            <Typography variant="body" className="text-muted-foreground">
              Recent Property Data Refresh
            </Typography>
            <Typography variant="body-semibold">{lastSyncDate}</Typography>
          </div>
        </div>
      )}
    </>
  );
};

export default BuildiumConnectionPanel;
